/**
 * Where the verification link lands (#26 / R6.1).
 *
 * **The measured problem this closes.** A family who signs up cannot sign in again:
 * `POST /api/auth/login` answers 403 *"Please verify your email address before logging in."*
 * — measured on the live preview 2026-09-08. The app shows them that sentence correctly. They
 * could not act on it, because the email links to `${APP_URL}/verify-email?token=…`
 * (`server/services/emailService.js:100`) and this app is a `HashRouter`, so a bare
 * `/verify-email` path was not one of its routes at all.
 *
 * `app/nginx.conf` now redirects that path into `#/verify-email?token=…`, and this is what it
 * lands on. Owner decision 2026-09-08: the rewrite lives on the Lantern proxy only, because the
 * legacy app shares `APP_URL` and repointing it would aim its own emails here.
 *
 * **The token is spent on arrival, exactly once.** `verifyEmail` is a GET, but it MUTATES — it
 * consumes a single-use token. React 18 mounts an effect twice in development StrictMode, so a
 * naive effect would burn the token and then report "already used" on the second call. The ref
 * guard below is not a style choice.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import LanternLoader from '../components/LanternLoader.jsx';
import { ApiError } from '../api/client.js';
import { resendVerification, verifyEmail } from '../api/session.js';

export const SIGN_IN_ROUTE = '/login';

const FAULT_MESSAGE = 'We could not check that link just now — please try it again in a moment.';

/**
 * One sentence for a verification that did not succeed.
 *
 * The server's own words are shown for its judgements about the TOKEN, because "this link has
 * expired" and "this link was already used" need different actions from the family, and only the
 * server knows which happened.
 */
export function verifyMessage(error) {
  if (!(error instanceof ApiError)) return FAULT_MESSAGE;
  if (error.status === 0) return error.message;
  if (error.status >= 400 && error.status < 500) {
    const fromBody = error.body?.error || error.body?.message;
    return typeof fromBody === 'string' && fromBody.trim() ? fromBody.trim() : FAULT_MESSAGE;
  }
  return FAULT_MESSAGE;
}

/** Read the token out of the hash route's own query string. */
export function tokenFrom(search) {
  return new URLSearchParams(search || '').get('token') || '';
}

export default function VerifyEmailRoute() {
  const location = useLocation();
  const token = tokenFrom(location.search);

  const [state, setState] = useState('checking');
  const [message, setMessage] = useState(null);
  const [resendState, setResendState] = useState('idle');
  // A single-use token must be spent once, even though StrictMode mounts effects twice.
  const spent = useRef(false);

  useEffect(() => {
    if (spent.current) return;
    spent.current = true;

    let alive = true;
    (async () => {
      try {
        await verifyEmail(token);
        if (alive) {
          setState('verified');
          setMessage(null);
        }
      } catch (error) {
        if (!alive) return;
        setState('failed');
        setMessage(verifyMessage(error));
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  const onResend = useCallback(async () => {
    // The endpoint allows 10 attempts per 15 minutes, so this never retries by itself.
    const email = window.prompt('Which email address should the new link go to?');
    if (!email) return;
    setResendState('sending');
    try {
      await resendVerification(email);
      // A 2xx means the server ACCEPTED the request. It does not prove delivery, and the copy
      // must not claim it did.
      setResendState('sent');
    } catch {
      setResendState('failed');
    }
  }, []);

  return (
    <main style={{ padding: '2rem', maxWidth: '34rem' }} data-component="VerifyEmail" data-state={state}>
      {state === 'checking' ? (
        <LanternLoader label="Checking your link — one moment." />
      ) : null}

      {state === 'verified' ? (
        <>
          <h1>Your email is verified</h1>
          <p>Thank you — your family is ready. You can sign in now.</p>
          <p>
            <Link to={SIGN_IN_ROUTE}>Sign in</Link>
          </p>
        </>
      ) : null}

      {state === 'failed' ? (
        <>
          <h1>That link did not work</h1>
          <p role="alert">{message}</p>
          <p>
            {resendState === 'sent' ? (
              // Deliberately not "we sent it". The server accepted the request; delivery is not
              // something this page can observe.
              'A new link has been requested. If that address is on file, it will arrive shortly.'
            ) : resendState === 'failed' ? (
              'We could not request a new link just now — please try again in a few minutes.'
            ) : (
              <button type="button" onClick={onResend} disabled={resendState === 'sending'}>
                {resendState === 'sending' ? 'Asking…' : 'Send me a new link'}
              </button>
            )}
          </p>
          <p>
            <Link to={SIGN_IN_ROUTE}>Back to sign in</Link>
          </p>
        </>
      ) : null}
    </main>
  );
}
